import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { authApi } from "@/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const emptyStudio = { city: "", name: "", address: "", phone: "+91-9899039199", hours: "Mon–Sat 10–8", map_url: "", order: 0, active: true };

export default function AdminStudios() {
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStudio);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    authApi.get("/admin/studios").then(({ data }) => { setStudios(data); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm({ ...emptyStudio, order: studios.length }); setOpen(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ ...s }); setOpen(true); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.city.trim() || !form.name.trim() || !form.address.trim()) {
      toast.error("City, name and address are required");
      return;
    }
    const payload = { ...form, order: parseInt(form.order, 10) || 0 };
    setSaving(true);
    try {
      if (editing === "new") await authApi.post("/admin/studios", payload);
      else await authApi.put(`/admin/studios/${editing}`, payload);
      toast.success("Studio saved");
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (s) => {
    await authApi.put(`/admin/studios/${s.id}`, { ...s, active: !s.active });
    setStudios((arr) => arr.map((x) => x.id === s.id ? { ...x, active: !x.active } : x));
  };

  const del = async (id) => {
    await authApi.delete(`/admin/studios/${id}`);
    toast.success("Studio deleted");
    setStudios((arr) => arr.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="admin-studios-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="heading-2">Fitting Studios</h1>
          <p className="text-muted-foreground text-sm mt-1">Shown on the "Find a Fitting" page · lower order shows first</p>
        </div>
        <button onClick={openNew} className="bg-[#E31837] text-white px-6 py-3 label-caps flex items-center gap-2 btn-transition hover:bg-[#b3122c] active:scale-95" data-testid="add-studio-btn">
          <Plus className="h-4 w-4" /> Add Studio
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground">Loading…</div>
      ) : studios.length === 0 ? (
        <div className="bg-white border border-border p-10 text-center text-muted-foreground">No studios yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {studios.map((s) => (
            <div key={s.id} className="bg-white border border-border p-5" data-testid={`studio-row-${s.id.slice(0, 8)}`}>
              <div className="flex items-start justify-between">
                <div>
                  <span className="label-caps text-[#E31837]">{s.city}</span>
                  <h3 className="font-head text-lg font-bold uppercase tracking-tight">{s.name}</h3>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(s)} className="p-2 hover:text-[#E31837] btn-transition" data-testid={`edit-studio-${s.id.slice(0, 8)}`} aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 hover:text-[#E31837] btn-transition" aria-label="Delete" data-testid={`delete-studio-${s.id.slice(0, 8)}`}><Trash2 className="h-4 w-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-none">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {s.name}?</AlertDialogTitle>
                        <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(s.id)} className="rounded-none bg-[#E31837] hover:bg-[#b3122c]">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2 flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0" /> {s.address}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.phone} · {s.hours}</p>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs label-caps text-muted-foreground">{s.active ? "Live" : "Hidden"}</span>
                <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`studio-toggle-${s.id.slice(0, 8)}`} />
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="studio-dialog">
          <DialogHeader><DialogTitle className="heading-3">{editing === "new" ? "Add Studio" : "Edit Studio"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-caps text-muted-foreground">City</Label>
                <Input value={form.city} onChange={(e) => setField("city", e.target.value)} className="mt-2 rounded-none" data-testid="studio-city" />
              </div>
              <div>
                <Label className="label-caps text-muted-foreground">Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setField("order", e.target.value)} className="mt-2 rounded-none" data-testid="studio-order" />
              </div>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Studio Name</Label>
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)} className="mt-2 rounded-none" data-testid="studio-name" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Address</Label>
              <Input value={form.address} onChange={(e) => setField("address", e.target.value)} className="mt-2 rounded-none" data-testid="studio-address" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Google Maps Link (optional)</Label>
              <Input value={form.map_url || ""} onChange={(e) => setField("map_url", e.target.value)} placeholder="https://maps.app.goo.gl/…" className="mt-2 rounded-none" data-testid="studio-map-url" />
              <p className="text-xs text-muted-foreground mt-1">Paste the studio's Google Maps share link. If empty, directions are auto-generated from the address.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-caps text-muted-foreground">Phone</Label>
                <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="mt-2 rounded-none" data-testid="studio-phone" />
              </div>
              <div>
                <Label className="label-caps text-muted-foreground">Hours</Label>
                <Input value={form.hours} onChange={(e) => setField("hours", e.target.value)} className="mt-2 rounded-none" data-testid="studio-hours" />
              </div>
            </div>
            <div className="flex items-center justify-between border border-border p-3">
              <Label className="label-caps">Live</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setField("active", v)} data-testid="studio-active" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving} className="bg-[#E31837] text-white px-6 py-3 label-caps btn-transition hover:bg-[#b3122c] disabled:opacity-60" data-testid="save-studio-btn">
              {saving ? "Saving…" : "Save Studio"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
